import type { AccountHistoryItem, FxAccountOverview } from "../../api/kurssicherung.ts";
import { kontoMischkursVerfuegbar } from "../../lib/fxKontostand.ts";
import { sicherungsNr } from "../../lib/sicherungsNummer.ts";
import { datum, datumBerlin, deltaText, kontoAnzeigenamen, mischkurs, usd, vergleicheBuchung } from "./fxAnzeige.ts";

export interface KontobewegungenTabelleProps {
  konten: readonly FxAccountOverview[];
  /** `null` = alle Konten. */
  aktiveKontoId: string | null;
  onKontoAuswaehlen: (accountId: string) => void;
  onAlleKontenAnzeigen: () => void;
}

/** Benennung des Vorgangs; "Sicherung S-n" immer mit der systemvergebenen Nummer. */
export function vorgangText(eintrag: AccountHistoryItem): string {
  const nr = eintrag.deal ? sicherungsNr(eintrag.deal) : "";
  if (eintrag.origin === "legacy") return `Sicherung ${nr} übernommen (Altbestand, Stand nicht erfasst)`;
  switch (eintrag.type) {
    case "migration_opening": return "Anfangsstand";
    case "hedge_accepted": return `Sicherung ${nr} übernommen`;
    case "manual_balance": return "Manuelle Korrektur";
    case "deal_deleted": return `Sicherung ${nr} gelöscht (nicht übernommen)`;
    case "migration_transfer_out": return "Kontotransfer";
    default: return eintrag.type;
  }
}

const zeitpunkt = (wert: string | null) => (wert ? new Date(wert).toLocaleString("de-DE", { timeZone: "Europe/Berlin" }) : "");

/**
 * Karte "Kontobewegungen": eine Tabelle statt der drei Register (Wechselhistorie, Kontostände, Alle Buchungen).
 * Reihenfolge = Buchungsreihenfolge (neueste Buchung zuerst, "Gebucht" = Erfassungszeit); die Bewegung kommt fertig vom
 * Server (`deltaUsd` = Stand danach - Stand davor desselben Kontos in genau dieser Reihenfolge). Damit ergibt "Stand danach"
 * der Zeile darunter plus Bewegung den Stand danach der Zeile. Der Stichtag ist eine eigene Spalte.
 */
export function KontobewegungenTabelle({ konten, aktiveKontoId, onKontoAuswaehlen, onAlleKontenAnzeigen }: KontobewegungenTabelleProps) {
  const kontoNamen = kontoAnzeigenamen(konten.map((konto) => konto.account));
  const zeilen = konten
    .filter((konto) => aktiveKontoId === null || konto.account.id === aktiveKontoId)
    .flatMap((konto) => konto.history.map((eintrag) => ({ eintrag, kontoName: kontoNamen.get(konto.account.id) ?? konto.account.name })))
    .sort((a, b) => vergleicheBuchung(b.eintrag, a.eintrag));

  return (
    <section className="fx-overview-card fx-bewegungen" aria-labelledby="fx-bewegungen-titel">
      <div className="fx-overview-card-head"><h2 id="fx-bewegungen-titel">Kontobewegungen</h2></div>
      <div className="fx-pills" role="group" aria-label="Konto">
        <button type="button" className={`fx-pill${aktiveKontoId === null ? " active" : ""}`} aria-pressed={aktiveKontoId === null} onClick={onAlleKontenAnzeigen}>Alle Konten</button>
        {konten.filter((konto) => konto.account.isActive !== false).map((konto) => <button type="button" key={konto.account.id} className={`fx-pill${aktiveKontoId === konto.account.id ? " active" : ""}`} aria-pressed={aktiveKontoId === konto.account.id} aria-label={`${konto.account.name} · ${konto.account.id}`} onClick={() => onKontoAuswaehlen(konto.account.id)}>{kontoNamen.get(konto.account.id) ?? konto.account.name}</button>)}
      </div>
      <div className="fx-tabelle-rahmen">
        <table className="fx-tabelle fx-bewegungen-tabelle">
          <thead><tr><th scope="col">Gebucht</th><th scope="col">Stichtag</th><th scope="col">Konto</th><th scope="col">Vorgang</th><th scope="col" className="fx-zahl">Bewegung USD</th><th scope="col" className="fx-zahl">Stand danach</th><th scope="col" className="fx-zahl">Mischkurs</th></tr></thead>
          <tbody>
            {zeilen.map(({ eintrag, kontoName }) => {
              const delta = deltaText(eintrag.deltaUsd);
              const wer = eintrag.actor ? `erfasst von ${eintrag.actor.label}${eintrag.recordedAt ? ` · ${zeitpunkt(eintrag.recordedAt)}` : ""}` : undefined;
              return (
                <tr key={eintrag.id} className={eintrag.type === "deal_deleted" ? "fx-zeile-geloescht" : undefined} data-testid="fx-bewegung">
                  <td title={eintrag.recordedAt ? zeitpunkt(eintrag.recordedAt) : "Altbestand: keine Erfassungszeit"}>{eintrag.recordedAt ? datumBerlin(eintrag.recordedAt) : "—"}</td>
                  <td>{datum(eintrag.effectiveOn)}</td>
                  <td>{kontoName}</td>
                  <td title={wer}>{vorgangText(eintrag)}{eintrag.actor ? <small className="fx-zweitzeile">{wer}</small> : null}</td>
                  <td className={`fx-zahl fx-delta-${delta.vorzeichen}`}>{delta.text}</td>
                  <td className="fx-zahl">{eintrag.balanceUsd !== null ? usd(eintrag.balanceUsd) : "—"}</td>
                  <td className="fx-zahl">{eintrag.balanceUsd !== null && eintrag.mixedRate !== null && kontoMischkursVerfuegbar(eintrag.balanceUsd) ? mischkurs(eintrag.mixedRate) : "—"}</td>
                </tr>
              );
            })}
            {!zeilen.length && <tr><td colSpan={7} className="fx-leer">Noch keine Kontobewegungen.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
